import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import config from '../../config';

interface ReportHeaderProps {
  reportTitle: string;
  reportTitleAr: string;
  reportDate?: string;
  filters?: {
    label: string;
    labelAr: string;
    value: string;
  }[];
}

const ReportHeader: React.FC<ReportHeaderProps> = ({
  reportTitle,
  reportTitleAr,
  reportDate,
  filters = []
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    loadCompanyInfo();
  }, []);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const logoUrl = companyInfo?.logoPath
    ? `${config.API_ORIGIN}${companyInfo.logoPath}`
    : null;

  return (
    <Box className="print-only" sx={{ mb: 3 }}>
      {/* Company Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: '3px solid',
          borderColor: 'primary.main',
        }}
      >
        {/* Company Logo */}
        {logoUrl && (
          <Box
            component="img"
            src={logoUrl}
            alt="Company Logo"
            sx={{
              height: 80,
              maxWidth: 120,
              objectFit: 'contain',
            }}
          />
        )}

        {/* Company Info */}
        <Box sx={{ flex: 1, mx: 3, textAlign: 'center' }}>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: 'primary.main', mb: 0.5 }}
          >
            {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
          </Typography>
          {companyInfo?.address && (
            <Typography variant="body2" color="text.secondary">
              {companyInfo?.address}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 0.5 }}>
            {companyInfo?.phone && (
              <Typography variant="body2" color="text.secondary">
                {isRTL ? 'هاتف:' : 'Tel:'} {companyInfo.phone}
              </Typography>
            )}
            {companyInfo?.email && (
              <Typography variant="body2" color="text.secondary">
                {isRTL ? 'بريد:' : 'Email:'} {companyInfo.email}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Spacer for balance */}
        {logoUrl && <Box sx={{ width: 120 }} />}
      </Box>

      {/* Report Title */}
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ color: 'primary.main' }}>
          {isRTL ? reportTitleAr : reportTitle}
        </Typography>
        {reportDate && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {isRTL ? 'التاريخ:' : 'Date:'} {reportDate}
          </Typography>
        )}
      </Box>

      {/* Filters Info */}
      {filters.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {filters.map((filter, index) => (
              <Typography key={index} variant="body2" color="text.secondary">
                <strong>{isRTL ? filter.labelAr : filter.label}:</strong> {filter.value}
              </Typography>
            ))}
          </Box>
          <Divider sx={{ mt: 1 }} />
        </Box>
      )}
    </Box>
  );
};

export default ReportHeader;